import { TextInputProps } from "@mantine/core";
import React, { useState, useEffect, useRef } from "react";
import { TextInput, ActionIcon, useMantineTheme } from "@mantine/core";

import { AppIcon } from "../AppIcon";

export interface SearchInputProps extends Omit<TextInputProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  debounceDelay?: number;
  onClear?: () => void;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  debounceDelay = 500,
  onClear,
  placeholder = "Search...",
  ...textInputProps
}) => {
  const theme = useMantineTheme();
  const [internalValue, setInternalValue] = useState(value);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync internal value with external value prop
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // ⚠ The timer calls the LATEST `onChange` and compares with the LATEST `value` (#129): it captured the render that
  // started it, so a parent that re-rendered meanwhile got its old handler called. Listing them instead would restart
  // the debounce on every parent render (an inline handler is new each time) and the search would never fire.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  // Debounce the onChange callback
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (internalValue !== valueRef.current) {
        onChangeRef.current(internalValue);
      }
    }, debounceDelay);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [internalValue, debounceDelay]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(event.currentTarget.value);
  };

  const handleClear = () => {
    setInternalValue("");
    onChange("");
    if (onClear) {
      onClear();
    }
  };

  return (
    <TextInput
      {...textInputProps}
      value={internalValue}
      onChange={handleChange}
      placeholder={placeholder}
      leftSection={
        <AppIcon iconName="Search" size={16} color={theme.colors.gray[6]} />
      }
      rightSection={
        internalValue ? (
          <ActionIcon
            variant="transparent"
            color="gray"
            onClick={handleClear}
            aria-label="Clear search"
            size="md"
          >
            <AppIcon iconName="X" size={14} color={theme.colors.gray[6]} />
          </ActionIcon>
        ) : null
      }
      size="md"
    />
  );
};
